import { G } from '@mobily/ts-belt';

import { arePrimaryAndSecondaryEmailsEquivalent } from '@Common/validators/email_secondary_email';

import type { ExtraValidationResult, ValidateFunc } from '@sh4res/bo-common/components/StepperForm';
import { isProdEnv } from '@sh4res/bo-common/utils/env';

const EMAIL_SAME_AS_SECONDARY_EMAIL_MESSAGE =
  'resources.bo_common.b2b.admins.actions.edit.validation.email_same_as_secondary_email';

type AdminEmailFields = {
  email?: unknown;
  secondary_email?: unknown;
};

const normalizeEmailValue = (value: unknown): string | null | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '') {
    return null;
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  return null;
};

const getEffectiveEmail = (formValue: unknown, recordValue: unknown): string | null => {
  const normalizedFormValue = normalizeEmailValue(formValue);

  if (normalizedFormValue !== undefined) {
    return normalizedFormValue;
  }

  return normalizeEmailValue(recordValue) ?? null;
};

export const validateAdminEmails = (
  emailFields: AdminEmailFields,
  record?: AdminEmailFields | null,
): ExtraValidationResult => {
  const effectivePrimary = getEffectiveEmail(emailFields.email, record?.email);
  const effectiveSecondary = getEffectiveEmail(
    emailFields.secondary_email,
    record?.secondary_email,
  );

  if (
    G.isNotNullable(effectivePrimary) &&
    G.isNotNullable(effectiveSecondary) &&
    arePrimaryAndSecondaryEmailsEquivalent(effectivePrimary, effectiveSecondary, {
      // Non-prod: allow plus-aliases so QA can receive real OTPs on distinct mailboxes.
      treatPlusAliasesAsSame: isProdEnv(),
    })
  ) {
    return {
      isValid: false,
      message: EMAIL_SAME_AS_SECONDARY_EMAIL_MESSAGE,
    };
  }

  return { isValid: true };
};

export const createAdminEmailsValidator =
  (record?: AdminEmailFields | null): ValidateFunc =>
  ({ getValues }) => {
    const { email, secondary_email } = getValues();

    return validateAdminEmails({ email, secondary_email }, record);
  };
